import React, { createRef } from "react";
import { useTranslation } from "react-i18next";
import { Box, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useStyles from "./data-change-box.style";
import arrowRightBold from "@iconify/icons-mdi/arrow-right-bold";
import { Icon } from "@iconify/react";
import { MyHrDataNewDataRequestBox } from "my-hr/components";
import { useOcComponentWidth } from "oc/hooks";
import { useOcIsMobile } from "oc/hooks/use-is-mobile";

interface MyHrDataChangeBoxProps {
  oldValue: string;
  newValue: string;
  type: string;
}

export const MyHrDataChangeBox: React.FC<MyHrDataChangeBoxProps> = ({
  oldValue,
  newValue,
  type,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const classes = useStyles();
  const isMobile = useOcIsMobile();

  const oldWidthRef = createRef<HTMLElement>() as React.RefObject<HTMLElement>;
  const newWidthRef = createRef<HTMLElement>() as React.RefObject<HTMLElement>;

  const [oldWidth] = useOcComponentWidth(oldWidthRef);
  const [newWidth] = useOcComponentWidth(newWidthRef);

  const exceedsThreshold = (oldWidth ?? 0) > 308 || (newWidth ?? 0) > 308;

  const oldLabel = (
    <Box className={classes.labelStyles}>
      <Typography
        variant="body1"
        sx={{
          textTransform: "capitalize",
          marginRight: "5px",
          color: theme.palette.white.main,
        }}
      >
        {t("OC_OLD")}
      </Typography>
      <Typography variant="body1" color={theme.palette.white.main}>
        {t(`MY_HR_${type?.toUpperCase()}`)}
      </Typography>
    </Box>
  );

  const newLabel = (
    <Box className={classes.labelStyles} style={{ textAlign: "center" }}>
      <Typography
        variant="body1"
        sx={{
          textTransform: "capitalize",
          marginRight: "5px",
          color: theme.palette.white.main,
        }}
      >
        {t("OC_NEW")}
      </Typography>
      <Typography variant="body1" color={theme.palette.white.main}>
        {t(`MY_HR_${type?.toUpperCase()}`)}
      </Typography>
    </Box>
  );

  return (
    <>
      <Box
        display="flex"
        flexDirection={exceedsThreshold || isMobile ? "column" : "row"}
        maxWidth={exceedsThreshold || isMobile ? "640px" : "unset"}
      >
        <Box
          // @ts-ignore
          className={`${classes.dataBox} ${
            // @ts-ignore
            (oldWidth ?? 0) > 308 ? classes.exceedsThreshold : ""
          }`}
          ref={oldWidthRef}
        >
          <MyHrDataNewDataRequestBox
            label={oldLabel}
            value={oldValue}
            isMobile={isMobile}
          />
        </Box>
        <Box height="10px" />
        <Box
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent:
              exceedsThreshold || isMobile ? "space-around" : "unset",
          }}
        >
          <Icon
            width="24px"
            height="24px"
            color={theme.palette.gray80.main}
            icon={arrowRightBold}
            style={{
              transform:
                exceedsThreshold || isMobile ? "rotate(90deg)" : "rotate(0deg)",
              marginBottom: exceedsThreshold ? "6px" : "unset",
            }}
          />
        </Box>
        <Box
          // @ts-ignore
          className={`${classes.dataBox} ${
            // @ts-ignore
            newWidth > 308 ? classes.exceedsThreshold : ""
          }`}
          ref={newWidthRef}
        >
          <MyHrDataNewDataRequestBox
            label={newLabel}
            value={newValue}
            isMobile={isMobile}
          />
        </Box>
      </Box>
    </>
  );
};

export default MyHrDataChangeBox;
